import { z } from "zod";

export const USER_ROLES = [
  {
    description:
      "Able to perform account tasks like adding/removing airplanes and users.",
    label: "Administrator",
    value: "ADMIN",
  },
  {
    description: "Able to add / edit and delete maintenance related items and resolve or close squawks.",
    label: "Airplane Maintainer",
    value: "MX",
  },
  {
    description:
      "Able to create pilot records, engine trending runs, and squawks; configure squawk visibility; and update maintenance intelligence. Can view maintenance records and reports.",
    label: "Pilot",
    value: "PILOT",
  },
  {
    description:
      "Able to add counter entries and create risk assessments. Can view organization details and airplanes.",
    label: "Crew Chief",
    value: "CREW_CHIEF",
  },
  {
    description: "Able to view but not able to add, edit or delete.",
    label: "Observer",
    value: "OBS",
  },
];

export type UserRoleOption = (typeof USER_ROLES)[number];

function getAssignedUserRoles(permissions: string[]): UserRoleOption[] {
  const normalized = new Set(permissions.map((permission) => permission.toUpperCase()));
  return USER_ROLES.filter((role) => normalized.has(role.value));
}

export enum USER_ROLES_ENUM {
  ADMIN = "ADMIN",
  MX = "MX",
  PILOT = "PILOT",
  CREW_CHIEF = "CREW_CHIEF",
  OBS = "OBS",
}

export const USER_ROLE_LABEL_CLASS_NAMES: Record<USER_ROLES_ENUM, string> = {
  [USER_ROLES_ENUM.ADMIN]: "mfp-InfoBoxTable-label--role-admin",
  [USER_ROLES_ENUM.MX]: "mfp-InfoBoxTable-label--role-mx",
  [USER_ROLES_ENUM.PILOT]: "mfp-InfoBoxTable-label--role-pilot",
  [USER_ROLES_ENUM.CREW_CHIEF]: "mfp-InfoBoxTable-label--role-crew-chief",
  [USER_ROLES_ENUM.OBS]: "mfp-InfoBoxTable-label--role-obs",
};

/** CSS custom properties used as role-label backgrounds (for contrast text). */
export const USER_ROLE_LABEL_BACKGROUND_VARS: Record<USER_ROLES_ENUM, string> = {
  [USER_ROLES_ENUM.ADMIN]: "--sky-800",
  [USER_ROLES_ENUM.MX]: "--sky-600",
  [USER_ROLES_ENUM.PILOT]: "--sky-700",
  [USER_ROLES_ENUM.CREW_CHIEF]: "--sky-500",
  [USER_ROLES_ENUM.OBS]: "--slate-500",
};

const USER_ROLE_VALUES = Object.values(USER_ROLES_ENUM) as [
  USER_ROLES_ENUM,
  ...USER_ROLES_ENUM[],
];

const zodUserRole = z.enum(USER_ROLE_VALUES);

export const zodPermissionsDataField = z
  .array(zodUserRole)
  .min(1, "Select at least one user permission");

function parsePermissionsDataFromApi(permissions: string[]): USER_ROLES_ENUM[] {
  return z.array(zodUserRole).parse(permissions);
}

export { getAssignedUserRoles, parsePermissionsDataFromApi };
